import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  Users, GraduationCap, Building2, BookOpen, ClipboardCheck, Award,
  AlertTriangle, CheckCircle2, Info, TrendingUp, ArrowLeft, RefreshCw,
} from "lucide-react";

/**
 * AdminHome — real-data only. All operational cards are populated by
 * /api/dashboard/stats (super_admin branch). On API failure we show an
 * error + retry state — no static fallback.
 */

const APPROVAL_ICONS = {
  application: ClipboardCheck,
  opportunity: BookOpen,
  association: Building2,
  certificate: Award,
};

function timeAgoAr(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "قبل لحظات";
  if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
  const d = Math.floor(diff / 86400);
  if (d < 7) return `قبل ${d} يوم`;
  return `قبل ${Math.floor(d / 7)} أسبوع`;
}

function KpiCard({ icon: Icon, label, value, tone = "teal", to }) {
  const tones = {
    teal: "bg-teal-50 text-teal-700", sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700", emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700", indigo: "bg-indigo-50 text-indigo-700",
  };
  const Wrapper = to ? Link : "div";
  const wp = to ? { to } : {};
  return (
    <Wrapper {...wp} className={`p-4 rounded-2xl bg-white border border-cream-200 card-shadow min-w-0 ${to ? "hover:-translate-y-0.5 hover:border-teal-200 transition-all block" : ""}`}>
      <div className={`h-9 w-9 rounded-xl grid place-items-center mb-2 sm:mb-4 ${tones[tone]}`}>
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.75} />
      </div>
      <div className="text-2xl font-black text-slate-900 arabic-numerals leading-tight">{value ?? 0}</div>
      <div className="text-xs sm:text-sm text-slate-500 mt-1 break-words">{label}</div>
    </Wrapper>
  );
}

function WeeklyStat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="h-9 w-9 rounded-lg bg-white/15 grid place-items-center mb-3">
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-2xl font-black arabic-numerals">{value ?? 0}</div>
      <div className="text-xs text-teal-100 mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data.stats);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div data-testid="admin-home-error" className="p-5 rounded-2xl bg-rose-50 border border-rose-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-rose-700 shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-rose-900">تعذّر تحميل بيانات اللوحة</h3>
            <p className="text-sm text-rose-800 mt-1">{error}</p>
            <button onClick={load} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-700 text-white text-sm font-semibold hover:bg-rose-600" data-testid="admin-home-retry">
              <RefreshCw className="h-4 w-4" /> إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  const approvals = stats?.pending_approvals || [];
  const alerts = stats?.system_alerts || [];
  const weekly = stats?.weekly_kpis || {};
  const top = stats?.top_associations || [];

  return (
    <div data-testid="admin-home">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة عمليات مدير النظام</div>
        <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">نظرة تشغيلية عامة</h1>
        <p className="mt-2 text-slate-600">قرارات اليوم في مكان واحد: موافقات معلّقة، تنبيهات النظام، وأداء الجمعيات.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
        <KpiCard icon={Users} label="المستخدمون" value={stats?.users} tone="teal" to="/dashboard/users" />
        <KpiCard icon={GraduationCap} label="الطلاب" value={stats?.students} tone="sky" />
        <KpiCard icon={Building2} label="الجمعيات" value={stats?.associations} tone="amber" />
        <KpiCard icon={BookOpen} label="الفرص" value={stats?.opportunities} tone="emerald" to="/dashboard/opportunities" />
        <KpiCard icon={ClipboardCheck} label="تسكين الطلاب" value={stats?.applications} tone="indigo" to="/dashboard/applications" />
        <KpiCard icon={Award} label="المقبولون" value={stats?.accepted_applications} tone="rose" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Pending Approvals — from DB */}
        <div className="lg:col-span-2 p-4 sm:p-6 rounded-2xl bg-white border border-cream-200 card-shadow min-w-0 max-w-full" data-testid="admin-approvals">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 min-w-0">
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                موافقات بانتظارك
                <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full arabic-numerals">
                  {approvals.length}
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-1">أحدث الفرص بانتظار المراجعة والطلبات المعلّقة</p>
            </div>
            <Link to="/dashboard/applications" className="text-xs font-semibold text-teal-700 hover:text-teal-800 inline-flex items-center gap-1 self-start sm:self-auto whitespace-nowrap">
              فتح كل الطلبات <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-cream-100">
            {approvals.length === 0 && !loading && (
              <div className="py-6 text-center text-sm text-slate-500">لا توجد موافقات بانتظارك حالياً</div>
            )}
            {approvals.map((a) => {
              const Icon = APPROVAL_ICONS[a.type] || ClipboardCheck;
              const to = a.type === "opportunity" ? "/dashboard/opportunities-queue" : "/dashboard/applications";
              return (
                <Link
                  key={a.id}
                  to={to}
                  className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 hover:bg-cream-50/50 rounded-lg -mx-2 px-2 min-w-0"
                  data-testid={`admin-approval-${a.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap min-w-0 sm:contents">
                    <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 grid place-items-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 rounded-full px-2 py-0.5 shrink-0 sm:hidden">
                      {a.label}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="hidden sm:flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 rounded-full px-2 py-0.5">
                        {a.label}
                      </span>
                      <div className="font-semibold text-slate-900 text-sm min-w-0 break-token" dir="auto">
                        <bdi>{a.subject}</bdi>
                      </div>
                    </div>
                    <div className="sm:hidden font-semibold text-slate-900 text-sm break-token" dir="auto">
                      <bdi>{a.subject}</bdi>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{timeAgoAr(a.submitted_at)}</div>
                  </div>
                  <span className="shrink-0 text-xs font-semibold text-teal-700 whitespace-nowrap self-start sm:self-auto min-h-[44px] inline-flex items-center px-2 -mx-2">مراجعة →</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* System Alerts — computed from DB */}
        <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-alerts">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-slate-900">تنبيهات النظام</h3>
            <p className="text-xs text-slate-500 mt-1">مؤشرات تستحق انتباهك</p>
          </div>
          <div className="space-y-2">
            {alerts.length === 0 && !loading && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> لا توجد تنبيهات نشطة
              </div>
            )}
            {alerts.map((a) => {
              const tone =
                a.level === "warning" ? "bg-amber-50 text-amber-800 border-amber-200"
                : a.level === "success" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-sky-50 text-sky-800 border-sky-200";
              const Icon = a.level === "warning" ? AlertTriangle : a.level === "success" ? CheckCircle2 : Info;
              return (
                <div key={a.id} className={`p-3 rounded-lg border ${tone} text-sm flex items-start gap-2`} data-testid={`admin-alert-${a.id}`}>
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">{a.text}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Weekly KPIs — computed last 7 days */}
        <div className="lg:col-span-2 p-4 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 text-white card-shadow" data-testid="admin-weekly">
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-widest text-teal-100">أداء الأسبوع</div>
            <h3 className="mt-1 text-xl font-heading font-black">مؤشرات آخر 7 أيام</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <WeeklyStat icon={Users} label="تسجيلات جديدة" value={weekly.new_signups} />
            <WeeklyStat icon={GraduationCap} label="إنجاز مقرر" value={weekly.completed_courses} />
            <WeeklyStat icon={Award} label="شهادات صادرة" value={weekly.issued_certificates} />
            <WeeklyStat icon={Building2} label="جمعيات نشطة" value={weekly.active_associations} />
            <WeeklyStat icon={ClipboardCheck} label="طلاب نشطون مُسكَّنون" value={weekly.active_placed_students} />
            <WeeklyStat icon={Users} label="طلاب نشطون بالمنصة" value={weekly.active_platform_students} />
          </div>
        </div>

        {/* Top Associations — aggregated by student count */}
        <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="admin-top-associations">
          <div className="mb-4">
            <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
              الجمعيات الأعلى أداءً <TrendingUp className="h-4 w-4 text-emerald-600" />
            </h3>
            <p className="text-xs text-slate-500 mt-1">حسب عدد الطلاب المُسنَدين وإتمام البرامج</p>
          </div>
          <div className="space-y-3">
            {top.length === 0 && !loading && (
              <div className="text-sm text-slate-500 py-4 text-center">لا توجد جمعيات نشطة بعد</div>
            )}
            {top.map((t) => (
              <div key={t.id} data-testid={`admin-top-assoc-${t.id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-900 truncate">{t.name}</span>
                  <span className="text-xs font-bold text-teal-700 arabic-numerals shrink-0">{t.completion_rate}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-cream-100 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-600 rounded-full" style={{ width: `${t.completion_rate}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 arabic-numerals">{t.students} طالباً</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
